<script setup lang="ts">
import { ref, computed } from 'vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import MercariProductList from '@/components/MercariProductList.vue'
import BaseTabs from '@/components/ui/baseTab/BaseTabs.vue'
import BaseTabItem from '@/components/ui/baseTab/BaseTabItem.vue'
import { getMercariUrl } from '@/lib/mercari'
import type {
	IMercariProductListFetchCompleteEvent,
	IMercariProductListItemClickEvent,
} from '@/components/MercariProductList.vue'
import type { ShopIdProductListTab } from './_MercariCampaign.vue'
import {
	MercariSearchMode,
	type MercariSearchParams,
	buildNavigationParams,
	formatTrackingKeyword,
} from '@/types/Mercari'

interface RedirectParams {
	searchQuery: string
	shop_id?: string
	shop_name?: string
}

const props = defineProps<{
	title: string
	subtitle: string
	keywords?: string[]
	tabs?: ShopIdProductListTab[]
	hasError?: boolean
	pageContent: any
}>()

const emit = defineEmits<{
	'fetch-complete': [event: IMercariProductListFetchCompleteEvent]
}>()

const productListRef = ref<InstanceType<typeof MercariProductList> | null>(null)

const currentTabIndex = ref(0)

const formattedKeywords = computed(() => {
	if (!props.keywords && !props.tabs) return []

	if (props.keywords?.length) {
		return props.keywords
	}

	if (props.tabs?.length) {
		return props.tabs.map(tab => tab.label || tab.keyword || '')
	}

	return []
})

const currentKeyword = computed(() => {
	if (props.keywords?.length) {
		return props.keywords[currentTabIndex.value] || ''
	}

	if (props.tabs?.length) {
		const tab = props.tabs[currentTabIndex.value]
		return tab.keyword || tab.label || ''
	}

	return ''
})

const currentShopId = computed(() => {
	if (props.tabs?.length) {
		return props.tabs[currentTabIndex.value].shop_id
	}
	return ''
})

const handleKeywordClick = (keyword: string, index: number) => {
	if (currentTabIndex.value !== index) {
		currentTabIndex.value = index
	}

	let eventExtra: string
	if (props.tabs) {
		const shopId = props.tabs.find(tab => tab.label === keyword)?.shop_id
		console.log('shopId', shopId)
		eventExtra = `shop_id_${shopId}`
	} else {
		eventExtra = `keyword_${keyword}`
	}

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariCampaign,
		button_name: 'Product Listing Tab',
		event_extra: eventExtra,
	})
}

const redirectToMercariProductSearchPage = (keyword?: string) => {
	if (keyword && props.tabs?.length) {
		const shopTab = props.tabs.find(tab => tab.shop_id === keyword)
		if (shopTab) {
			const searchParams: MercariSearchParams = {
				mode: MercariSearchMode.SHOP,
				shop_id: keyword,
				shop_name: shopTab.label,
			}
			const navigationParams = buildNavigationParams(searchParams)
			const redirectUrl = getMercariUrl('search', navigationParams)

			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.MercariCampaign,
				button_name: 'ViewMore_ProductList',
			})

			window.location.href = redirectUrl
			return
		}
	}

	const searchKeyword = keyword || currentKeyword.value

	let searchParams: MercariSearchParams
	if (currentShopId.value) {
		searchParams = {
			mode: MercariSearchMode.SHOP,
			shop_id: currentShopId.value,
			shop_name: props.tabs?.[currentTabIndex.value]?.label,
		}
	} else {
		searchParams = {
			mode: MercariSearchMode.KEYWORD,
			keyword: searchKeyword,
		}
	}

	const navigationParams = buildNavigationParams(searchParams)
	const redirectUrl = getMercariUrl('search', navigationParams)

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariCampaign,
		button_name: 'ViewMore_ProductList',
	})

	window.location.href = redirectUrl
}

const handleFetchComplete = (event: IMercariProductListFetchCompleteEvent) => {
	emit('fetch-complete', event)
}

const handleItemClick = (event: IMercariProductListItemClickEvent) => {
	const params = new URLSearchParams(window.location.search)
	const slug = params.get('slug')

	if (slug) {
		const targetUrl = new URL(event.targetUrl, window.location.origin)
		targetUrl.searchParams.set('source', slug)
		window.history.replaceState(
			{},
			'',
			`${window.location.pathname}?${params.toString()}`,
		)

		window.open(targetUrl.href, '_blank')
	} else {
		window.open(event.targetUrl, '_blank')
	}

	createGAEvent('select_item', {
		page_name: GaGeneralPageName.MercariCampaign,
		items: [
			{
				location_id: GaGeneralPageName.MercariCampaign,
				item_list_name: 'Product Listing-Campaign',
				item_id: `product_${event.item.id}`,
				item_name: event.item.name,
				item_category: 'Mercari',
				index: event.index + 1,
				keyword: formatTrackingKeyword(
					!!currentShopId.value,
					currentShopId.value,
					currentKeyword.value,
				),
			},
		],
	})
}

defineExpose({
	productListRef,
})
</script>

<template>
	<div class="flex flex-col justify-center gap-casual-friends">
		<template v-if="!hasError">
			<div class="flex flex-col gap-strong-friends">
				<h2 class="typography-h3-str">
					{{ title }}
				</h2>
				<h4 class="typography-s1-reg">
					{{ subtitle }}
				</h4>
			</div>

			<div class="flex justify-center">
				<div class="no-scrollbar w-full max-w-full overflow-x-auto">
					<ul class="flex min-w-min justify-center">
						<BaseTabs size="medium" type="secondary">
							<BaseTabItem
								v-for="(keyword, index) of formattedKeywords"
								:key="keyword"
								type="secondary"
								size="medium"
								@click="handleKeywordClick(keyword, index)"
								:isSelected="index === currentTabIndex"
							>
								{{ keyword }}
							</BaseTabItem>
						</BaseTabs>
					</ul>
				</div>
			</div>
		</template>

		<MercariProductList
			ref="productListRef"
			:key="currentKeyword"
			:keyword="currentKeyword"
			:shopId="currentShopId"
			:locationId="GaGeneralPageName.MercariCampaign"
			:viewMoreMode="'redirect'"
			:item-content="props.pageContent.productItem"
			:edge-case="props.pageContent.edgeCase"
			:load-more-button-text="props.pageContent.productListLoadMoreButtonText"
			@fetch-complete="handleFetchComplete"
			@item-click="handleItemClick"
			@view-more-click="redirectToMercariProductSearchPage"
		/>
	</div>
</template>
